import { useIsPreviewOrDemo } from "../../contexts/IsPreviewOrDemoContext";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import WeddingRSVPForm from "../../guest-management/forms/RSVPForm/Wedding/WeddingRSVPForm";
import { RSVP_SESSION_STORAGE_KEY } from "../../guest-management/forms/RSVPForm/constants";
import RSVPSearchModal from "../../guest-management/forms/RSVPSearchModal/RSVPSearchModal";
import { Configuration, RSVPData } from "../../resources/modelTypes";
import { css } from "@emotion/react";
import { Button as FancyButton } from "@minted/fancyclothes";
import { styleUtils, TextInput, theme } from "@minted/minted-components";
import React, { memo, useCallback, useState } from "react";

const rsvpStyles = {
  cookieError: css`
    ${theme.typeStyles.bodyMediumStyles};
    text-align: center;
    color: ${theme.colors.negative800};
    margin-bottom: ${theme.spacing.x2};
  `,
  nameInput: css`
    margin-bottom: ${theme.spacing.x4};
    text-align: left;
  `,
  wrapper: css`
    max-width: ${styleUtils.rem(500)};
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${theme.spacing.x4};
  `,
};

export type DisplayRSVPProps = {
  configuration: Configuration;
};

const DisplayRSVP = ({ configuration }: DisplayRSVPProps) => {
  const site = useCurrentSite();
  const { isDemo, isPreview } = useIsPreviewOrDemo();
  const [rsvpData, setRsvpData] = useState<RSVPData | null>(() => {
    const stored = sessionStorage.getItem(RSVP_SESSION_STORAGE_KEY);

    if (stored) {
      const { contact, events, guests } = JSON.parse(stored);

      if (contact?.id && events && guests) {
        return { contact, events, guests };
      }
    }

    return null;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const isInBrowser = typeof window !== "undefined";
  const hasCookies = isInBrowser && window.navigator.cookieEnabled;

  const openModal = useCallback(() => {
    setShowRSVPModal(true);
  }, []);
  const closeModal = useCallback(() => {
    setShowRSVPModal(false);
  }, []);

  const updateSearchTerm = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);
  const onKeyDown = useCallback(
    (event) => event.key === "Enter" && setShowRSVPModal(true),
    [],
  );

  return rsvpData ? (
    <WeddingRSVPForm
      contact={rsvpData.contact}
      events={rsvpData.events}
      guests={rsvpData.guests}
      hasExistingRsvps={rsvpData.hasExistingRsvps}
      onCancelRsvp={() => setRsvpData(null)}
    />
  ) : (
    <div css={rsvpStyles.wrapper}>
      {configuration.rsvpSubheading || "Enter Your Name to RSVP"}
      {hasCookies ? (
        <>
          {showRSVPModal && (
            <RSVPSearchModal
              closeModal={closeModal}
              configuration={configuration}
              initialSearchTerm={searchTerm}
              openInvitation={false}
              setRsvpData={setRsvpData}
              site={site}
            />
          )}
          <div css={rsvpStyles.nameInput}>
            <TextInput
              disabled={isPreview || isDemo}
              onChange={updateSearchTerm}
              onKeyDown={onKeyDown}
              size="small"
              touched
              value={searchTerm}
            />
          </div>
          <FancyButton
            block={true}
            disabled={isPreview || isDemo}
            onClick={openModal}
            text="Find RSVP"
          />
        </>
      ) : (
        <div css={rsvpStyles.cookieError}>
          Cookies are required to submit your RSVP. Please enable cookies in
          your browser.
        </div>
      )}
    </div>
  );
};

const MemoizedDisplayRSVP = memo(DisplayRSVP);

export { MemoizedDisplayRSVP as DisplayRSVP };
